<script setup>
/**
 * pages/vouchers/index.vue — Addendum 07, Modul T
 * ─────────────────────────────────────────────────────────
 * CRUD voucher/kupon. Pemakaian sesungguhnya saat checkout (Modul V) belum
 * dikerjakan di modul ini — halaman ini murni kelola data + generator kode.
 */
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'
import { useAbility } from '@casl/vue'
import { formatRupiah, formatDateOnly } from '@/composables/useFormat'
import { requiredValidator } from '@core/utils/validators'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Voucher',
  },
})

const ability = useAbility()
const { loading, errors, request } = useApiRequest()

// -----------------------------------------------------------------------
// State
// -----------------------------------------------------------------------
const vouchers = ref([])
const search = ref('')
const activeFilter = ref(null) // null=semua, true, false
const isFetching = ref(false)
const pagination = ref({ total: 0, current_page: 1, last_page: 1 })

const dialog = ref(false)
const dialogMode = ref('create')
const dialogLoading = ref(false)
const deleteDialog = ref(false)
const selectedItem = ref(null)

const typeOptions = [
  { title: 'Persentase (%)', value: 'percentage' },
  { title: 'Nominal (Rp)', value: 'fixed_amount' },
]

const defaultForm = () => ({
  code: '',
  type: 'percentage',
  value: null,
  min_purchase_amount: null,
  max_discount_amount: null,
  usage_limit: null,
  usage_limit_per_customer: 1,
  starts_at: null,
  ends_at: null,
  is_active: true,
})

const form = ref(defaultForm())
const formRef = ref()

const headers = [
  { title: 'Kode', key: 'code' },
  { title: 'Tipe & Nilai', key: 'value', sortable: false },
  { title: 'Kuota', key: 'usage', sortable: false },
  { title: 'Periode', key: 'period', sortable: false },
  { title: 'Status', key: 'is_active', width: '100px' },
  { title: 'Aksi', key: 'actions', sortable: false, width: '100px', align: 'end' },
]

// -----------------------------------------------------------------------
// Fetch
// -----------------------------------------------------------------------
const fetchVouchers = async (page = 1) => {
  isFetching.value = true
  try {
    const params = { page }
    if (search.value) params.search = search.value
    if (activeFilter.value !== null) params.is_active = activeFilter.value ? 1 : 0

    const response = await useApi('/api/v1/vouchers', { query: params }).get()
    const data = response?.data ?? response

    vouchers.value = data?.data ?? []
    pagination.value = {
      total: data?.pagination?.total ?? 0,
      current_page: data?.pagination?.current_page ?? 1,
      last_page: data?.pagination?.last_page ?? 1,
    }
  } finally {
    isFetching.value = false
  }
}

// -----------------------------------------------------------------------
// Dialog helpers
// -----------------------------------------------------------------------
const openCreate = () => {
  dialogMode.value = 'create'
  form.value = defaultForm()
  formRef.value?.reset()
  dialog.value = true
}

const openEdit = item => {
  dialogMode.value = 'edit'
  selectedItem.value = item
  form.value = {
    code: item.code,
    type: item.type,
    value: item.value,
    min_purchase_amount: item.min_purchase_amount,
    max_discount_amount: item.max_discount_amount,
    usage_limit: item.usage_limit,
    usage_limit_per_customer: item.usage_limit_per_customer,
    starts_at: item.starts_at ? item.starts_at.slice(0, 10) : null,
    ends_at: item.ends_at ? item.ends_at.slice(0, 10) : null,
    is_active: item.is_active,
  }
  dialog.value = true
}

const confirmDelete = item => {
  selectedItem.value = item
  deleteDialog.value = true
}

// Kode dibiarkan kosong -> auto-generate random di backend. Ditandai
// eksplisit "AUTO" di sini murni UX (placeholder), bukan nilai yang dikirim.
const clearCode = () => {
  form.value.code = ''
}

// -----------------------------------------------------------------------
// CRUD
// -----------------------------------------------------------------------
const submitForm = async () => {
  const { valid } = await formRef.value?.validate()
  if (!valid) return

  dialogLoading.value = true

  const res = await request(async () => {
    if (dialogMode.value === 'create') {
      return await useApi('/api/v1/vouchers').post({ body: form.value })
    } else {
      return await useApi(`/api/v1/vouchers/${selectedItem.value.id}`).put({ body: form.value })
    }
  })

  dialogLoading.value = false

  if (res) {
    dialog.value = false
    await fetchVouchers(pagination.value.current_page)
  }
}

const deleteVoucher = async () => {
  const res = await request(() =>
    useApi(`/api/v1/vouchers/${selectedItem.value.id}`).delete(),
  )

  if (res !== null) {
    deleteDialog.value = false
    await fetchVouchers(pagination.value.current_page)
  }
}

// -----------------------------------------------------------------------
// Display helpers
// -----------------------------------------------------------------------
const valueLabel = item => item.type === 'percentage'
  ? `${item.value}%`
  : formatRupiah(item.value)

const usageLabel = item => {
  const used = item.usage_count ?? 0
  const limit = item.usage_limit ?? '∞'

  return `${used} / ${limit}`
}

const periodLabel = item => {
  if (!item.starts_at && !item.ends_at) return 'Selalu aktif'

  const start = item.starts_at ? formatDateOnly(item.starts_at) : '...'
  const end = item.ends_at ? formatDateOnly(item.ends_at) : '...'

  return `${start} — ${end}`
}

// -----------------------------------------------------------------------
// Lifecycle
// -----------------------------------------------------------------------
onMounted(() => fetchVouchers())

let searchTimer = null
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => fetchVouchers(1), 400)
})

watch(activeFilter, () => fetchVouchers(1))
</script>

<template>
  <div>
    <VRow class="mb-2">
      <VCol cols="12">
        <h4 class="text-h4 font-weight-medium mb-1">Voucher & Kupon</h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Kelola kode voucher diskon persentase/nominal dengan batas kuota pemakaian.
        </p>
      </VCol>
    </VRow>

    <VCard>
      <VCardText class="d-flex align-center gap-3 flex-wrap">
        <VTextField
          v-model="search"
          placeholder="Cari kode voucher..."
          prepend-inner-icon="ri-search-line"
          density="compact"
          hide-details
          clearable
          style="max-width: 280px"
        />
        <VSelect
          v-model="activeFilter"
          :items="[
            { title: 'Semua Status', value: null },
            { title: 'Aktif', value: true },
            { title: 'Nonaktif', value: false },
          ]"
          density="compact"
          hide-details
          style="max-width: 180px"
        />
        <VSpacer />
        <VBtn
          v-if="ability.can('create', 'Voucher')"
          color="primary"
          prepend-icon="ri-add-line"
          @click="openCreate"
        >
          Tambah Voucher
        </VBtn>
      </VCardText>

      <VDivider />

      <VDataTable
        :headers="headers"
        :items="vouchers"
        :loading="isFetching"
        hover
        no-data-text="Belum ada voucher"
      >
        <template #item.code="{ item }">
          <VChip color="primary" variant="tonal" size="small" class="font-weight-medium">
            {{ item.code }}
          </VChip>
        </template>

        <template #item.value="{ item }">
          <span class="font-weight-medium">{{ valueLabel(item) }}</span>
          <div v-if="item.max_discount_amount" class="text-caption text-medium-emphasis">
            Maks. {{ formatRupiah(item.max_discount_amount) }}
          </div>
        </template>

        <template #item.usage="{ item }">
          <span class="text-body-2">{{ usageLabel(item) }}</span>
          <div class="text-caption text-medium-emphasis">
            Maks. {{ item.usage_limit_per_customer }}x/customer
          </div>
        </template>

        <template #item.period="{ item }">
          <span class="text-body-2">{{ periodLabel(item) }}</span>
        </template>

        <template #item.is_active="{ item }">
          <VChip size="small" :color="item.is_active ? 'success' : 'error'" variant="tonal">
            {{ item.is_active ? 'Aktif' : 'Nonaktif' }}
          </VChip>
        </template>

        <template #item.actions="{ item }">
          <VBtn
            v-if="ability.can('update', 'Voucher')"
            icon size="small" variant="text" color="primary"
            @click="openEdit(item)"
          >
            <VIcon icon="ri-edit-line" />
            <VTooltip activator="parent">Edit</VTooltip>
          </VBtn>
          <VBtn
            v-if="ability.can('delete', 'Voucher')"
            icon size="small" variant="text" color="error"
            @click="confirmDelete(item)"
          >
            <VIcon icon="ri-delete-bin-line" />
            <VTooltip activator="parent">Hapus</VTooltip>
          </VBtn>
        </template>

        <template #no-data>
          <div class="text-center pa-8">
            <VIcon icon="ri-coupon-3-line" size="64" color="grey-lighten-1" />
            <p class="text-body-1 mt-3 mb-1">Belum ada voucher</p>
            <VBtn
              v-if="ability.can('create', 'Voucher')"
              color="primary" variant="tonal"
              @click="openCreate"
            >
              Tambah Voucher Pertama
            </VBtn>
          </div>
        </template>
      </VDataTable>

      <VCardText v-if="pagination.last_page > 1" class="d-flex justify-center">
        <VPagination
          :model-value="pagination.current_page"
          :length="pagination.last_page"
          @update:model-value="fetchVouchers"
        />
      </VCardText>
    </VCard>

    <!-- ================================================================
         Dialog: Create / Edit
    ================================================================ -->
    <VDialog v-model="dialog" max-width="560" persistent>
      <VCard>
        <VCardTitle class="pa-4 pb-0 d-flex align-center justify-space-between">
          <span>{{ dialogMode === 'create' ? 'Tambah Voucher' : 'Edit Voucher' }}</span>
          <VBtn icon size="small" variant="text" @click="dialog = false">
            <VIcon icon="ri-close-line" />
          </VBtn>
        </VCardTitle>

        <VForm ref="formRef" @submit.prevent="submitForm">
          <VCardText class="pt-3">
            <VRow>
              <VCol cols="12">
                <VTextField
                  v-model="form.code"
                  label="Kode Voucher"
                  placeholder="Kosongkan untuk generate otomatis"
                  :readonly="dialogMode === 'edit'"
                  :hint="dialogMode === 'edit' ? 'Kode tidak bisa diubah setelah dibuat.' : 'Kosongkan untuk kode acak otomatis.'"
                  persistent-hint
                  :error-messages="errors.code"
                  autofocus
                >
                  <template v-if="dialogMode === 'create' && form.code" #append-inner>
                    <VBtn icon size="x-small" variant="text" title="Kosongkan" @click="clearCode">
                      <VIcon icon="ri-close-line" />
                    </VBtn>
                  </template>
                </VTextField>
              </VCol>

              <VCol cols="12" sm="6">
                <VSelect
                  v-model="form.type"
                  :items="typeOptions"
                  label="Tipe Diskon"
                  :rules="[requiredValidator]"
                  :error-messages="errors.type"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VTextField
                  v-model.number="form.value"
                  :label="form.type === 'percentage' ? 'Nilai (%)' : 'Nilai (Rp)'"
                  type="number"
                  min="0"
                  :max="form.type === 'percentage' ? 100 : undefined"
                  step="any"
                  :rules="[requiredValidator]"
                  :error-messages="errors.value"
                />
              </VCol>

              <VCol cols="12" sm="6">
                <VTextField
                  v-model.number="form.min_purchase_amount"
                  label="Min. Pembelian (opsional)"
                  type="number"
                  min="0"
                  step="any"
                  :error-messages="errors.min_purchase_amount"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VTextField
                  v-model.number="form.max_discount_amount"
                  label="Maks. Potongan (opsional)"
                  type="number"
                  min="0"
                  step="any"
                  hint="Penting untuk tipe persentase"
                  persistent-hint
                  :error-messages="errors.max_discount_amount"
                />
              </VCol>

              <VCol cols="12" sm="6">
                <VTextField
                  v-model.number="form.usage_limit"
                  label="Kuota Total (opsional)"
                  type="number"
                  min="1"
                  step="1"
                  hint="Kosongkan = tidak terbatas"
                  persistent-hint
                  :error-messages="errors.usage_limit"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VTextField
                  v-model.number="form.usage_limit_per_customer"
                  label="Maks. Pemakaian per Customer"
                  type="number"
                  min="1"
                  step="1"
                  hint="Cuma berlaku untuk transaksi dengan customer terdaftar"
                  persistent-hint
                  :rules="[requiredValidator]"
                  :error-messages="errors.usage_limit_per_customer"
                />
              </VCol>

              <VCol cols="12" sm="6">
                <VTextField
                  v-model="form.starts_at"
                  label="Mulai (opsional)"
                  type="date"
                  :error-messages="errors.starts_at"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VTextField
                  v-model="form.ends_at"
                  label="Berakhir (opsional)"
                  type="date"
                  :error-messages="errors.ends_at"
                />
              </VCol>

              <VCol cols="12">
                <VSwitch
                  v-model="form.is_active"
                  label="Aktif"
                  color="success"
                  density="compact"
                  hide-details
                />
              </VCol>
            </VRow>
          </VCardText>

          <VDivider />

          <VCardActions class="pa-4">
            <VSpacer />
            <VBtn variant="text" @click="dialog = false">Batal</VBtn>
            <VBtn type="submit" color="primary" :loading="dialogLoading">
              {{ dialogMode === 'create' ? 'Simpan' : 'Update' }}
            </VBtn>
          </VCardActions>
        </VForm>
      </VCard>
    </VDialog>

    <!-- ================================================================
         Dialog: Delete
    ================================================================ -->
    <VDialog v-model="deleteDialog" max-width="440">
      <VCard>
        <VCardTitle class="text-h6 pa-4 pb-0">Hapus Voucher?</VCardTitle>
        <VCardText class="pa-4 pt-2">
          Hapus voucher <strong>{{ selectedItem?.code }}</strong>?
          Voucher yang sudah pernah dipakai di transaksi akan dinonaktifkan saja, bukan dihapus.
        </VCardText>
        <VCardActions class="pa-4 pt-0">
          <VSpacer />
          <VBtn variant="text" @click="deleteDialog = false">Batal</VBtn>
          <VBtn color="error" :loading="loading" @click="deleteVoucher">Hapus</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
